import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Users, Plus, X, AlertCircle, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { axiosInstance } from '../../lib/api-client';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';

interface Plan {
  _id: string;
  name: string;
  price: number;
  duration: number;
}

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isBlocked: boolean;
  role: string;
}

interface BulkResult {
  successful: Array<{
    userId: string;
    userName: string;
    orderId: string;
  }>;
  failed: Array<{
    userId: string;
    userName?: string;
    reason: string;
  }>;
}

interface Settings {
  maxDiscountPercentages: number[];
  maxExtraContacts: number[];
  maxExtraValidity: number[];
  commissionPercentage: number;
}

export function BulkPromotionalOrderCreator() {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);

  // Form state
  const [planId, setPlanId] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState(10);
  const [extraContacts, setExtraContacts] = useState(0);
  const [extraValidity, setExtraValidity] = useState(0);
  const [durationInMinutes, setDurationInMinutes] = useState(10080); // 7 days
  const [campaignName, setCampaignName] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const [usersRes, plansRes, settingsRes] = await Promise.all([
        axiosInstance.get('/admin/users', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axiosInstance.get('/subscriptions/plans', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axiosInstance.get('/promotional-orders/settings', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (usersRes.data.success) {
        // Only show regular users who are not blocked
        const regularUsers = usersRes.data.data.users.filter(
          (u: User) => u.role === 'user' && !u.isBlocked
        );
        setUsers(regularUsers);
      }

      if (plansRes.data.success) {
        setPlans(plansRes.data.data || []);
      }

      if (settingsRes.data.success) {
        setSettings(settingsRes.data.data);
      }
    } catch (error: any) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const filteredUserIds = filteredUsers.map(u => u._id);
    if (selectedUsers.length === filteredUserIds.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUserIds);
    }
  };

  const handleCreateBulk = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    if (!planId) {
      toast.error('Please select a plan');
      return;
    }

    const selectedPlan = plans.find(p => p._id === planId);
    if (!selectedPlan) {
      toast.error('Invalid plan selected');
      return;
    }

    const discountedPrice = selectedPlan.price * (1 - discountPercentage / 100);

    try {
      setCreating(true);
      const token = localStorage.getItem('token');

      const response = await axiosInstance.post(
        '/promotional-orders/bulk',
        {
          userIds: selectedUsers,
          planId,
          discountedPrice,
          extraContacts,
          extraValidity,
          durationInMinutes,
          campaignName,
          notes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setBulkResult(response.data.data);
        toast.success(response.data.message);
        setShowForm(false);
        setSelectedUsers([]);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create bulk promotional orders');
    } finally {
      setCreating(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(query) ||
      user.lastName?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.phone?.includes(query)
    );
  });

  const selectedPlan = plans.find(p => p._id === planId);
  const discountedPrice = selectedPlan ? selectedPlan.price * (1 - discountPercentage / 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-3 rounded-lg">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Bulk Promotional Orders</h2>
            <p className="text-sm text-gray-600">Create promotional offers for multiple users at once</p>
          </div>
        </div>
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Bulk Orders
          </Button>
        )}
      </div>

      {/* Results Display */}
      {bulkResult && (
        <div className="mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Bulk Creation Results</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkResult(null)}
            >
              <X className="w-4 h-4 mr-1" />
              Close
            </Button>
          </div>

          {/* Successful */}
          {bulkResult.successful.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h4 className="font-semibold text-green-900">
                  Successfully Created ({bulkResult.successful.length})
                </h4>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {bulkResult.successful.map((item, idx) => (
                  <div key={idx} className="text-sm text-green-800 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{item.userName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Failed */}
          {bulkResult.failed.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="w-5 h-5 text-red-600" />
                <h4 className="font-semibold text-red-900">
                  Failed ({bulkResult.failed.length})
                </h4>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {bulkResult.failed.map((item, idx) => (
                  <div key={idx} className="text-sm text-red-800">
                    <div className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium">{item.userName || item.userId}</div>
                        <div className="text-xs text-red-600">{item.reason}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="border-2 border-purple-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Configure Promotional Offer</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowForm(false);
                setSelectedUsers([]);
              }}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label>Select Plan *</Label>
              <select
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Choose a plan</option>
                {plans.map(plan => (
                  <option key={plan._id} value={plan._id}>
                    {plan.name} - ₹{plan.price}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Discount Percentage *</Label>
              <select
                value={discountPercentage}
                onChange={(e) => setDiscountPercentage(Number(e.target.value))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                {settings?.maxDiscountPercentages.map(pct => (
                  <option key={pct} value={pct}>{pct}%</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Extra Contacts</Label>
              <select
                value={extraContacts}
                onChange={(e) => setExtraContacts(Number(e.target.value))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                {settings?.maxExtraContacts.map(count => (
                  <option key={count} value={count}>{count}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Extra Validity (days)</Label>
              <select
                value={extraValidity}
                onChange={(e) => setExtraValidity(Number(e.target.value))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                {settings?.maxExtraValidity.map(days => (
                  <option key={days} value={days}>{days} days</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Offer Duration (minutes)</Label>
              <Input
                type="number"
                value={durationInMinutes}
                onChange={(e) => setDurationInMinutes(Number(e.target.value))}
                min="60"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                {Math.floor(durationInMinutes / 60 / 24)} days, {Math.floor((durationInMinutes / 60) % 24)} hours
              </p>
            </div>

            <div>
              <Label>Campaign Name</Label>
              <Input
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g., Summer Special 2024"
                className="mt-1"
              />
            </div>

            <div className="md:col-span-2">
              <Label>Notes</Label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes about this campaign..."
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                rows={3}
              />
            </div>
          </div>

          {/* Pricing Preview */}
          {selectedPlan && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-purple-900 mb-2">Pricing Preview</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-purple-600">Original Price:</span>
                  <div className="font-bold text-purple-900">₹{selectedPlan.price}</div>
                </div>
                <div>
                  <span className="text-purple-600">Discount ({discountPercentage}%):</span>
                  <div className="font-bold text-red-600">-₹{(selectedPlan.price * discountPercentage / 100).toFixed(2)}</div>
                </div>
                <div>
                  <span className="text-purple-600">Discounted Price:</span>
                  <div className="font-bold text-green-600">₹{discountedPrice.toFixed(2)}</div>
                </div>
              </div>
            </div>
          )}

          {/* User Selection */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <Label>Select Users ({selectedUsers.length} selected)</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedUsers.length === filteredUsers.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            <Input
              placeholder="Search users by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mb-4"
            />

            <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No users found</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredUsers.map(user => (
                    <label
                      key={user._id}
                      className="flex items-center gap-3 p-4 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user._id)}
                        onChange={() => handleUserToggle(user._id)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-600">{user.email}</div>
                        {user.phone && (
                          <div className="text-xs text-gray-500">{user.phone}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3">
            <Button
              onClick={handleCreateBulk}
              disabled={creating || selectedUsers.length === 0 || !planId}
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating for {selectedUsers.length} users...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Create for {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowForm(false);
                setSelectedUsers([]);
              }}
            >
              Cancel
            </Button>
          </div>

          {selectedUsers.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  This will create {selectedUsers.length} promotional order{selectedUsers.length !== 1 ? 's' : ''}.
                  Each order will be active for {Math.floor(durationInMinutes / 60 / 24)} days.
                  Users with existing active promotional orders will be skipped automatically.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
